export const Dark = '#000';
export const Light = '#FFF';

/**
 * isColour is true for a CSS colour. Schools enter their colours, thus a value
 * that is not a colour (for example, one with `;` or `}`) must not reach CSS.
 */
export function isColour(value: string | undefined | null): value is string {
  return !!value && CSS.supports('color', value);
}

/**
 * isLight calculates the perceived brightness of a colour, and returns whether
 * it is perceived as a light or dark colour. It gives false for a value that
 * is not a hex colour.
 *
 * @param hexColour
 * @returns {Boolean}
 */
export function isLight(hexColour: string): boolean {
  const rgb = hexToRgb(hexColour);
  if (!rgb) {
    return false;
  }
  const [r, g, b] = rgb;

  // HSP (Highly Sensitive Perceived Brightness) equation.
  // refer: http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  return hsp > 127.5;
}

/**
 * hexToRgb converts a hex colour to an array containing RGB. It accepts #rgb,
 * #rgba, #rrggbb and #rrggbbaa, with or without the #, and ignores the alpha.
 * It gives undefined for a value that is not a hex colour.
 *
 * @param {String} hex
 * @returns {int[]|undefined}
 */
export function hexToRgb(hex: string): [number, number, number] | undefined {
  let digits = hex.trim().replace(/^#/, '');
  if (digits.length === 3 || digits.length === 4) {
    digits = digits.replace(/./g, '$&$&');
  }
  if (!/^([0-9a-f]{6}|[0-9a-f]{8})$/i.test(digits)) {
    return undefined;
  }

  const num = parseInt(digits.slice(0, 6), 16);
  return [num >> 16, (num >> 8) & 255, num & 255];
}

// lighten adds amount to each channel. It gives a value that is not a hex
// colour back with no change, thus CSS ignores it and uses its fallback.
export function lighten(colour: string, amount: number): string {
  const rgb = hexToRgb(colour);
  if (!rgb) {
    return colour;
  }

  const shift = (c: number) =>
    Math.max(0, Math.min(255, Math.round(c + amount)));
  const [r, g, b] = rgb;
  // padStart keeps 6 digits when the red channel is below 16.
  return (
    (colour.trim().startsWith('#') ? '#' : '') +
    ((shift(r) << 16) | (shift(g) << 8) | shift(b))
      .toString(16)
      .padStart(6, '0')
  );
}

export function darken(colour: string, amount: number): string {
  return lighten(colour, -1 * amount);
}

/**
 * opacify converts a hex string to rgba() format, adding the specified alpha
 * value. It gives a value that is not a hex colour back with no change.
 *
 * @param {String} hexColour
 * @param {String} alpha
 * @returns {String}
 */
export function opacify(hexColour: string, alpha: string): string {
  const rgb = hexToRgb(hexColour);
  if (!rgb) {
    return hexColour;
  }
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
